import React, { useMemo, useState, useEffect, useCallback } from "react"
import { useDebounce } from "hooks/useDebounce"
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Card,
    CardBody,
    Row,
    Col,
} from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"

const EntitySelectionModal = ({
    isOpen,
    toggle,
    title = "Select Item",
    data = [],
    columns = [],
    onItemSelect,
    selectedItemId = null,
    isLoading = false,
    emptyMessage = "No items found",
    idField = "id",
    onFetchData = null,
    searchPlaceholder = "Search items...",
    pageSize = 10,
    refreshContextUri = null,
    history = null,
}) => {
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)

    // Fetch data when modal opens
    useEffect(() => {
        if (isOpen && onFetchData) {
            onFetchData({
                search: "",
                page: 1,
            })
            setSearch("")
            setPage(1)
        }
    }, [isOpen, onFetchData])

    const dispatchSearch = useCallback((value) => {
        setPage(1)
        if (onFetchData) {
            onFetchData({
                search: value || "",
                page: 1,
            })
        }
    }, [onFetchData])

    const [debouncedSearch] = useDebounce(dispatchSearch)

    // Handle search change - following your exact pattern
    const handleSearchChange = e => {
        const { value } = e.target
        setSearch(value)
        debouncedSearch(value)
    }

    // Handle page change - following your exact pattern
    const handlePageChange = newPage => {
        setPage(newPage)
        if (onFetchData) {
            onFetchData({
                search: search || "",
                page: newPage,
            })
        }
    }

    const handleRefresh = () => {
        if (onFetchData) {
            onFetchData({
                search: search || "",
                page,
            })
        }
    }

    // Add selection action column to the provided columns - matching brands-list.js pattern
    const enhancedColumns = useMemo(() => {
        const actionColumn = {
            Header: "Action",
            Cell: cellProps => {
                return (
                    <div>
                        <IconButton
                            icon="bx bx-selection"
                            onClick={() => handleItemSelect(cellProps)}
                            title="Select"
                        />
                    </div>
                )
            },
        }

        // Return columns with action column at the end
        return [...columns, actionColumn]
    }, [columns])

    const handleItemSelect = (cellProps) => {
        const item = cellProps.row.original
        onItemSelect(item)
        toggle()
    }

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl">
            <ModalHeader toggle={toggle}>{title}</ModalHeader>
            <ModalBody>
                <Card>
                    <CardBody>
                        {/* Search Row - following your exact pattern */}
                        <Row className="mb-3">
                            <Col md="6">
                                <div>
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={search}
                                        onChange={handleSearchChange}
                                        className="form-control w-75 rounded-pill"
                                    />
                                </div>
                            </Col>
                        </Row>

                        {/* Table with built-in pagination and refresh */}
                        <TableContainer
                            columns={enhancedColumns}
                            data={data?.error?.message ? [] : data}
                            isGlobalFilter={false}
                            isAddUserList={true}
                            customPageSize={pageSize}
                            className="custom-header-css"
                            columnFilter={false}
                            changePageSize={false}
                            onPageChange={handlePageChange}
                            currentPage={page}
                            hasNextPage={!data?.error?.message && data.length >= pageSize}
                            onRefresh={handleRefresh}
                            refreshContextUri={refreshContextUri}
                            refreshActionType="get"
                            history={history}
                        />
                    </CardBody>
                </Card>
            </ModalBody>
            <ModalFooter>
                <CButton.Secondary text="Cancel" onClick={toggle} />
            </ModalFooter>
        </Modal>
    )
}

export default EntitySelectionModal